import React, {useEffect} from 'react';

import {useHistory, useParams} from 'react-router-dom';

import Login from './components/pages/auth/login';
import LoginHelp from './components/pages/auth/loginHelp';
import ForgotPassword from './components/pages/auth/forgotPassword';
import ForgotPasswordConfirmation from './components/pages/auth/forgotPasswordConfirmation';
import PasswordLinkExpired from './components/pages/auth/PasswordLinkExpired';
import ChangePassword from './components/pages/auth/changePassword';
import Browse from './components/pages/browse';
import CoachAI from './components/pages/coach-ai';
import Home from './components/pages/home';
import Profile from './components/pages/profile';
import Playlist from './components/pages/playlist';
import Search from './components/pages/search';
import UserSettings from './components/pages/userSettings';
import Inbox from './components/pages/inbox';
import Unauthorised from './components/pages/errors/Unauthorised';
import StandaloneSkillsProfilePage from './components/pages/profile/StandaloneSkillsProfilePage';

import {DEFAULT_EDIT_PLAYLIST_ROUTES} from 'screens/SinglePlaylistManager';

import {
	COACH_AI_ROUTE,
	MY_TEAMS_ROUTES,
	PEOPLE_ROUTES,
	PLATFORM_ANNOUNCEMENTS_ROUTE,
	PROFILE_ROUTES,
} from 'utils/routes';
import {isHelixOnlyHub} from 'utils/helix';
import config from 'utils/config';

const MyTeam = React.lazy(
	() => import(/* webpackChunkName: "myteam" */ './components/pages/myTeam')
);
const MyTeams = React.lazy(
	() => import(/* webpackChunkName: "myteams" */ './components/pages/myTeams')
);
const MyTeamMember = React.lazy(
	() =>
		import(
			/* webpackChunkName: "myteammember" */ './components/pages/myTeam/people/member'
		)
);

const PeopleSkillsPage = React.lazy(
	() =>
		import(
			/* webpackChunkName: "peopleskillspage" */ './components/pages/people/skills'
		)
);

const MyPlaylistStats = React.lazy(
	() =>
		import(
			/* webpackChunkName: "myteam" */ './components/pages/myTeam/playlists'
		)
);
const UserProgressAgainstPlaylist = React.lazy(
	() =>
		import(
			/* webpackChunkName: "myteam" */ './components/pages/myTeam/playlists/userPlaylistProgress'
		)
);
const MyAssetStats = React.lazy(
	() =>
		import(
			/* webpackChunkName: "myteam" */ './components/pages/myTeam/assets'
		)
);
const PlaylistEditorPage = React.lazy(
	() =>
		import(
			/* webpackChunkName: "playlist-editor" */ './components/pages/playlist/PlaylistEditor'
		)
);

const PlatformAnnouncementsPage = React.lazy(
	() =>
		import(
			/* webpackChunkName: "platform-announcements" */ './components/pages/platform-announcements'
		)
);

export const HOME_ROUTE = isHelixOnlyHub ? PROFILE_ROUTES.skills : '/home';

// At some point it would probably make sense to convert our flat
// route config to a more conventional nested structure. Probably
// in conjunction with an update to React Router 6 or React Location.

const publicRoutes = [
	{
		path: ['/', '/login'],
		component: Login,
		exact: true,
	},
	{
		path: '/login-help',
		component: LoginHelp,
		exact: true,
	},
	{
		path: '/forgot',
		component: ForgotPassword,
		exact: true,
	},
	{
		path: '/password-requested',
		component: ForgotPasswordConfirmation,
		exact: true,
	},
	{
		path: '/password-link-expired',
		component: PasswordLinkExpired,
		exact: true,
	},
	{
		path: '/change-password',
		component: ChangePassword,
		exact: true,
	},
	{
		path: '/unauthorised',
		component: Unauthorised,
		exact: true,
	},
];

const PeopleSkillsRoutes = {
	path: [
		PEOPLE_ROUTES.skills,
		PEOPLE_ROUTES.skillsSkill,
		PEOPLE_ROUTES.skillsMeeting,
	],
	component: PeopleSkillsPage,
	exact: true,
};

const privateRoutes = [
	{
		path: '/home',
		component: Home,
		exact: true,
	},
	{
		path: [
			PROFILE_ROUTES.root,
			PROFILE_ROUTES.playlists,
			PROFILE_ROUTES.assets,
			PROFILE_ROUTES.badges,
			PROFILE_ROUTES.skills,
			PROFILE_ROUTES.skillsSkill,
			PROFILE_ROUTES.skillsMeeting,
			// legacy routes
			'/profile/favourites',
		],
		component: isHelixOnlyHub ? StandaloneSkillsProfilePage : Profile,
	},
	{
		path: ['/browse', '/browse/:topicId'],
		component: Browse,
		exact: true,
	},
	{
		path: '/search',
		component: Search,
		exact: true,
	},
	{
		path: '/settings',
		component: UserSettings,
		exact: true,
	},
	{
		path: '/inbox',
		component: Inbox,
		exact: true,
	},
	{
		path: COACH_AI_ROUTE,
		component: CoachAI,
		exact: true,
	},
	{
		path: PLATFORM_ANNOUNCEMENTS_ROUTE,
		component: PlatformAnnouncementsPage,
		exact: true,
	},
	{
		path: MY_TEAMS_ROUTES.root,
		component: MyTeams,
		exact: true,
	},
	{
		path: [MY_TEAMS_ROUTES.teamMember, MY_TEAMS_ROUTES.member],
		component: MyTeamMember,
		exact: true,
	},
	{
		path: [
			'/playlists/:playlistId/analytics',
			'/playlists/:playlistId/analytics/:teamId',
		],
		component: MyPlaylistStats,
		exact: true,
	},
	{
		path: [
			'/assets/:assetId/analytics',
			'/assets/:assetId/analytics/:teamId',
		],
		component: MyAssetStats,
		exact: true,
	},
	{
		path: '/user/:userId/playlist/:playlistId',
		component: UserProgressAgainstPlaylist,
		exact: true,
	},
	{
		path: [
			MY_TEAMS_ROUTES.team,
			MY_TEAMS_ROUTES.teamTrends,
			MY_TEAMS_ROUTES.teamPeople,
			MY_TEAMS_ROUTES.teamReport,
			MY_TEAMS_ROUTES.teamReports,
			MY_TEAMS_ROUTES.teamPlaylists,
		],
		component: MyTeam,
	},
	{
		path: [
			DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistDetails,
			DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistContent,
			DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistContentPreview,
			DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistShare,
			DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistAccess,
			DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistAnalytics,
		],
		component: PlaylistEditorPage,
	},
	{
		path: ['/playlists/:id', '/playlists/:id/comments'],
		component: Playlist,
	},
	{
		path: '/assets/:id',
		component: RedirectAsset,
		exact: true,
	},
];

if (!config.helixAnonymiseUsers) {
	privateRoutes.push(PeopleSkillsRoutes);
}

// This component is a workaround for a limitation in React Router:
// Normally we'd use a regular Redirect component, but that doesn't
// allow mapping URL parameters to a query string _while also_
// attaching location state.
function RedirectAsset() {
	const history = useHistory();
	const {id} = useParams();
	useEffect(() => {
		history.replace({
			pathname: '/home',
			search: `?asset=${id}`,
			state: {
				wasRedirected: true,
			},
		});
	});
	return null;
}

const publicRoutePaths = publicRoutes
	.flatMap(route => route.path)
	.filter(Boolean);

export {publicRoutes, privateRoutes, publicRoutePaths};
